import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AuftragsAkteResponse, AuftragsAkteRechnung, AuftragKalkulation } from '../types';
import { KATEGORIE_LABEL, Steuersatz } from './settings/sections/SteuersatzSection';
import { useAktiveModule } from '../hooks/useAktiveModule';
import { useRechte } from '../hooks/useRechte';
import { darfBelegeSenden, darfKorrekturbelege, darfLieferscheineBuchen, darfRechnungFestschreiben } from '../lib/rechte';
import {
  betragAnzeige, bezugText, istKorrektur, kannGutschreiben, kannStornieren, normalisiereArt,
} from '../lib/rechnungsart';
import { downloadDateiOderFehler } from '../lib/download';
import { FESTSCHREIBEN_FRAGE, fehlerText } from '../lib/belegKarte';
import { kannZuPartnerNavigieren, navigiereZuPartner } from '../lib/navigation';
import BelegAnsichtModal from './BelegAnsichtModal';
import RechnungErstellenDialog from './RechnungErstellenDialog';
import GutschriftDialog from './GutschriftDialog';
import StornoDialog from './StornoDialog';
import RechnungsartBadge from './RechnungsartBadge';
import Timeline from './Timeline';
import AnhangPanel from './AnhangPanel';
import NotizenAufgaben from './NotizenAufgaben';
import DokumenteKarte from './DokumenteKarte';
import { vertragStatusKind } from './VertragAkte';
import RegieberichtForm, { RegieberichtKarte, leseRolleAusSpeicher } from './RegieberichtForm';
import './AuftragsAkte.css';
import './RechnungsartDialoge.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface AuftragsAkteProps {
  auftragId: number;
  onBack: () => void;
  /** Vertragsakte öffnen (Karte „Vertrag“); ohne Callback kein Button. */
  onOpenVertrag?: (vertragId: number) => void;
}

const TYP_LABEL: Record<string, string> = {
  PRODUKTION: 'Produktion',
  DIENSTLEISTUNG: 'Dienstleistung / Handwerk',
  HANDEL: 'Handel',
  PROJEKT: 'Projektgeschäft',
};

const ABSCHLUSS_STATI = ['Geliefert', 'Fakturiert'];

function statusKind(status: string | null | undefined): 'offen' | 'fortschritt' | 'abschluss' | 'storniert' {
  // Auftrag liefert 'Storniert', Rechnung/Lieferschein 'STORNIERT'
  if (status?.toUpperCase() === 'STORNIERT') return 'storniert';
  if (status && ABSCHLUSS_STATI.includes(status)) return 'abschluss';
  if (status === 'Angebot') return 'offen';
  return 'fortschritt';
}

function einsatzStatusKind(status: string | null | undefined): 'offen' | 'fortschritt' | 'abschluss' | 'storniert' {
  if (status === 'STORNIERT') return 'storniert';
  if (status === 'ABGESCHLOSSEN') return 'abschluss';
  if (status === 'GEPLANT') return 'offen';
  return 'fortschritt';
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('de-DE');
}

function formatPrice(price?: number | null): string {
  if (price === null || price === undefined) return '-';
  return `${price.toFixed(2)} €`;
}

function prioClass(p: number): string {
  return p >= 7 ? 'high' : p >= 4 ? 'medium' : 'low';
}

/** Ampel für den Deckungsbeitrag: 'over' (Ist > Soll), 'warn' (Marge < 10%
 * vom Soll), sonst 'ok'. Bei soll_gesamt <= 0 zählt nur, ob überschritten. */
function deckungsbeitragAmpel(deckungsbeitrag: number, sollGesamt: number): 'ok' | 'warn' | 'over' {
  if (deckungsbeitrag < 0) return 'over';
  if (sollGesamt > 0 && deckungsbeitrag / sollGesamt < 0.1) return 'warn';
  return 'ok';
}

const nr4 = (id: number) => String(id).padStart(4, '0');

type BelegAnsichtState = { url: string; title: string } | null;

const AuftragsAkte: React.FC<AuftragsAkteProps> = ({ auftragId, onBack, onOpenVertrag }) => {
  const [akte, setAkte] = useState<AuftragsAkteResponse | null>(null);
  const [statusOptionen, setStatusOptionen] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusAktion, setStatusAktion] = useState<string | null>(null);
  const [statusFehler, setStatusFehler] = useState<string | null>(null);

  const [sendenId, setSendenId] = useState<number | null>(null);
  const [sendenFehler, setSendenFehler] = useState<string | null>(null);
  const [lieferscheinFehler, setLieferscheinFehler] = useState<string | null>(null);
  // Rechnungsdialog (E-Rechnung Paket 1/3): Rechnungsart, Leistungszeitpunkt (§14 Abs. 4 Nr. 6 UStG),
  // Abschlag/Schluss — eigene Komponente RechnungErstellenDialog.
  const [rechnungDialogOffen, setRechnungDialogOffen] = useState(false);
  // Gutschrift-/Storno-Dialog zu einer Rechnung (höchstens einer offen)
  const [gutschriftFuer, setGutschriftFuer] = useState<AuftragsAkteRechnung | null>(null);
  const [stornoFuer, setStornoFuer] = useState<AuftragsAkteRechnung | null>(null);
  const [rechnungMeldung, setRechnungMeldung] = useState<string | null>(null);

  // Beleg-Steuersatz (E-Rechnung Paket 1): Override für den ganzen Auftrag,
  // z.B. §13b. Die Liste kommt aus den Steuersatz-Stammdaten.
  const [steuersaetze, setSteuersaetze] = useState<Steuersatz[]>([]);
  const [steuersatzSpeichert, setSteuersatzSpeichert] = useState(false);
  const [steuersatzFehler, setSteuersatzFehler] = useState<string | null>(null);

  const [downloadFehler, setDownloadFehler] = useState('');
  const [belegAnsicht, setBelegAnsicht] = useState<BelegAnsichtState>(null);

  const [kalkulation, setKalkulation] = useState<AuftragKalkulation | null>(null);
  const [materialDetailOffen, setMaterialDetailOffen] = useState(false);
  const [zeitDetailOffen, setZeitDetailOffen] = useState(false);
  const [quelleBerichtOffen, setQuelleBerichtOffen] = useState(false);

  const aktiveModule = useAktiveModule();
  const { rechte } = useRechte();
  const authToken = localStorage.getItem('auth_token') || '';
  // Die Akte bekommt (anders als ManagementPage) keine Props von oben — die
  // Rolle steht im selben localStorage-Eintrag, den App.tsx beim Login setzt.
  // Ohne sie blendete die RegieberichtKarte ihre Abrechnungs-Aktionen auch
  // für Berechtigte aus (darfAbrechnen ist fail-closed).
  const userRolle = leseRolleAusSpeicher();

  const modulAktiv = useCallback(
    (modul: string) => aktiveModule.size === 0 || aktiveModule.has(modul),
    [aktiveModule]
  );

  const ladeAkte = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get<AuftragsAkteResponse>(`${API_URL}/api/auftraege/${auftragId}/akte`);
      setAkte(res.data);

      const typ = res.data.auftrag.auftrag_typ;
      if (typ) {
        try {
          const so = await axios.get(`${API_URL}/api/auftraege/status-optionen?auftrag_typ=${typ}`);
          setStatusOptionen(so.data.status || []);
        } catch {
          setStatusOptionen([]);
        }
      }

      try {
        const kalk = await axios.get<AuftragKalkulation>(`${API_URL}/api/auftraege/${auftragId}/kalkulation`);
        setKalkulation(kalk.data);
      } catch {
        // Nachkalkulation ist eine Zusatz-Kachel — ein Fehler dort blockiert
        // nicht die restliche Auftragsakte.
        setKalkulation(null);
      }
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err)
        ? (err.response?.data?.detail ?? err.message)
        : 'Unbekannter Fehler';
      setError(`Auftragsakte konnte nicht geladen werden: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [auftragId]);

  useEffect(() => {
    ladeAkte();
  }, [ladeAkte]);

  const handleStatusChange = async (zielStatus: string) => {
    setStatusFehler(null);
    setStatusAktion(zielStatus);
    try {
      await axios.put(`${API_URL}/api/auftraege/${auftragId}/status`, { status: zielStatus });
      await ladeAkte();
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setStatusFehler(err.response.data?.detail || 'Übergang nicht erlaubt.');
      } else {
        setStatusFehler('Status konnte nicht geändert werden.');
      }
    } finally {
      setStatusAktion(null);
    }
  };

  useEffect(() => {
    axios.get<Steuersatz[]>(`${API_URL}/api/steuersaetze`)
      .then(r => setSteuersaetze(r.data))
      .catch(() => setSteuersaetze([]));
  }, []);

  const handleSteuersatzAendern = async (wert: string) => {
    setSteuersatzFehler(null);
    setSteuersatzSpeichert(true);
    try {
      await axios.put(`${API_URL}/api/auftraege/${auftragId}`, {
        steuersatz_id: wert === '' ? null : parseInt(wert, 10),
      });
      await ladeAkte();
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err)
        ? (err.response?.data?.detail ?? 'Steuersatz konnte nicht gesetzt werden.')
        : 'Steuersatz konnte nicht gesetzt werden.';
      setSteuersatzFehler(msg);
    } finally {
      setSteuersatzSpeichert(false);
    }
  };

  const openRechnungAnsicht = (r: AuftragsAkteRechnung) => {
    setBelegAnsicht({
      url: `${API_URL}/api/dokumente/ausgangsrechnungen/${r.ausgangsrechnung_id}/beleg-ansicht`,
      title: `Rechnung ${r.nummer_anzeige}`,
    });
  };

  /** Entwurf festschreiben (Nummer, Status VERSENDET) ohne E-Mail-Versand. */
  const rechnungFestschreiben = async (r: AuftragsAkteRechnung) => {
    if (!window.confirm(FESTSCHREIBEN_FRAGE)) return;
    setSendenFehler(null);
    setSendenId(r.ausgangsrechnung_id);
    try {
      await axios.post(`${API_URL}/api/dokumente/ausgangsrechnungen/${r.ausgangsrechnung_id}/festschreiben`);
      await ladeAkte();
    } catch (err: unknown) {
      const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
      setSendenFehler(fehlerText(detail, 'Rechnung konnte nicht festgeschrieben werden.'));
    } finally {
      setSendenId(null);
    }
  };

  const rechnungSenden = async (r: AuftragsAkteRechnung) => {
    setSendenFehler(null);
    setSendenId(r.ausgangsrechnung_id);
    try {
      await axios.post(`${API_URL}/api/email/ausgangsrechnung/${r.ausgangsrechnung_id}`, {});
      await ladeAkte();
    } catch (err: unknown) {
      const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
      setSendenFehler(
        typeof detail === 'string' ? detail
          : (typeof detail?.message === 'string' ? detail.message : 'Rechnung konnte nicht gesendet werden.'),
      );
    } finally {
      setSendenId(null);
    }
  };

  /** Nach einem Rechnungs-Dialog (Gutschrift/Storno): Meldung zeigen, Akte neu laden. */
  const rechnungDialogFertig = async (meldung: string) => {
    setGutschriftFuer(null);
    setStornoFuer(null);
    setRechnungMeldung(meldung);
    await ladeAkte();
  };

  /** Rechnungsentwurf verwerfen (noch ohne Nummer, daher Löschen statt Storno). */
  const rechnungEntwurfVerwerfen = async (r: AuftragsAkteRechnung) => {
    if (!window.confirm(`${r.nummer_anzeige} verwerfen? Der Entwurf wird gelöscht.`)) return;
    setSendenFehler(null);
    setSendenId(r.ausgangsrechnung_id);
    try {
      await axios.delete(`${API_URL}/api/dokumente/ausgangsrechnungen/${r.ausgangsrechnung_id}`);
      await ladeAkte();
    } catch (err: unknown) {
      const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
      setSendenFehler(fehlerText(detail, 'Entwurf konnte nicht verworfen werden.'));
    } finally {
      setSendenId(null);
    }
  };

  /** Lieferschein stornieren; ein gebuchter Lagerabgang wird zurückgebucht. */
  const lieferscheinStornieren = async (lieferscheinId: number, nr: string) => {
    if (!window.confirm(`Lieferschein ${nr} stornieren? Bereits gebuchte Mengen gehen zurück ins Lager.`)) return;
    setLieferscheinFehler(null);
    try {
      await axios.post(`${API_URL}/api/lieferscheine/${lieferscheinId}/stornieren`);
      await ladeAkte();
    } catch (err: unknown) {
      const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
      setLieferscheinFehler(fehlerText(detail, 'Lieferschein konnte nicht storniert werden.'));
    }
  };

  /**
   * Beleg-PDF herunterladen. Ansehen bleibt der Beleg-Ansicht vorbehalten
   * (openAngebotAnsicht & Co.) — die zeigt denselben Beleg als Dokument,
   * ohne den Umweg über ein PDF.
   */
  const belegHerunterladen = async (
    typ: 'angebot' | 'auftragsbestaetigung' | 'lieferschein', kuerzel: string, bezeichnung: string,
  ) => {
    setDownloadFehler('');
    const fehler = await downloadDateiOderFehler(
      `${API_URL}/api/dokumente/auftraege/${auftragId}/${typ}/pdf`,
      `${kuerzel}_${nr4(auftragId)}.pdf`,
      bezeichnung,
    );
    if (fehler) setDownloadFehler(fehler);
  };

  const lieferscheinHerunterladen = () => belegHerunterladen('lieferschein', 'LS', 'Lieferschein');
  const angebotHerunterladen = () => belegHerunterladen('angebot', 'AN', 'Angebot');
  const auftragsbestaetigungHerunterladen = () =>
    belegHerunterladen('auftragsbestaetigung', 'AB', 'Auftragsbestätigung');

  const openAngebotAnsicht = () => {
    setBelegAnsicht({
      url: `${API_URL}/api/auftraege/${auftragId}/beleg-ansicht?typ=angebot`,
      title: `Angebot ${nr4(auftragId)}`,
    });
  };

  const openAuftragsbestaetigungAnsicht = () => {
    setBelegAnsicht({
      url: `${API_URL}/api/auftraege/${auftragId}/beleg-ansicht?typ=auftragsbestaetigung`,
      title: `Auftragsbestätigung ${nr4(auftragId)}`,
    });
  };

  const rechnungHerunterladen = async (r: AuftragsAkteRechnung) => {
    setDownloadFehler('');
    const fehler = await downloadDateiOderFehler(
      `${API_URL}/api/dokumente/ausgangsrechnungen/${r.ausgangsrechnung_id}/pdf`,
      `RE_${r.nummer_anzeige.replace(/[#\s]+/g, '_')}.pdf`,
      'Rechnung',
    );
    if (fehler) setDownloadFehler(fehler);
  };

  if (loading) {
    return (
      <div className="aa-container">
        <div className="aa-loading">Auftragsakte wird geladen…</div>
      </div>
    );
  }

  if (error || !akte) {
    return (
      <div className="aa-container">
        <button className="aa-back-btn" onClick={onBack}>← Zurück</button>
        <div className="aa-error">{error || 'Auftragsakte nicht gefunden.'}</div>
      </div>
    );
  }

  const { auftrag } = akte;
  const stepStati = statusOptionen.filter((s) => s !== 'Storniert');
  const istStorniert = auftrag.status === 'Storniert';
  const currentIndex = stepStati.indexOf(auftrag.status);
  const naechsteSchritte = auftrag.naechste_schritte || [];

  const lieferstatusPositionen = akte.lieferstatus?.positionen || [];
  const lieferPct = lieferstatusPositionen.length
    ? Math.round(
        (lieferstatusPositionen.reduce((s, p) => s + Math.min(p.menge_geliefert, p.menge_bestellt), 0) /
          Math.max(
            1,
            lieferstatusPositionen.reduce((s, p) => s + p.menge_bestellt, 0)
          )) *
          100
      )
    : 0;

  return (
    <div className="aa-container">
      {/* Kopf */}
      <div className="aa-kopf">
        <button className="aa-back-btn" onClick={onBack}>← Zurück</button>
        <div className="aa-kopf-info">
          <h2 className="aa-auftrag-nr">{auftrag.auftrag_nr}</h2>
          {auftrag.kunde_id != null && kannZuPartnerNavigieren() ? (
            <button type="button" className="k-btn k-btn--ghost k-btn--sm" title="Kundenakte öffnen"
                    onClick={() => navigiereZuPartner(auftrag.kunde_id)}>
              {auftrag.kunde?.name || '-'}
            </button>
          ) : (
            <span className="aa-kunde">{auftrag.kunde?.name || '-'}</span>
          )}
          {auftrag.projekt_id != null && (
            <span className="aa-baustelle">Baustelle: {auftrag.projekt_bezeichnung || `#${auftrag.projekt_id}`}</span>
          )}
          {auftrag.quelle_regiebericht && (
            <button type="button" className="k-btn k-btn--ghost k-btn--sm"
                    title="Quell-Regiebericht öffnen"
                    onClick={() => setQuelleBerichtOffen(true)}>
              aus {auftrag.quelle_regiebericht.bericht_nr}
            </button>
          )}
          <span className={`aa-prio-badge aa-prio-${prioClass(auftrag.prioritaet)}`}>
            Priorität {auftrag.prioritaet}
          </span>
          <label className="aa-steuersatz" title="Beleg-Steuersatz für alle Positionen dieses Auftrags (z.B. Reverse-Charge §13b). Leer = je Artikel.">
            <span>Steuersatz</span>
            <select
              value={auftrag.steuersatz_id != null ? String(auftrag.steuersatz_id) : ''}
              onChange={e => handleSteuersatzAendern(e.target.value)}
              disabled={steuersatzSpeichert || istStorniert}
            >
              <option value="">je Artikel</option>
              {steuersaetze
                .filter(s => s.aktiv || s.steuersatz_id === auftrag.steuersatz_id)
                .map(s => (
                  <option key={s.steuersatz_id} value={String(s.steuersatz_id)}>
                    {s.bezeichnung} ({s.prozent.toLocaleString('de-DE')} %{s.kategorie !== 'S' ? `, ${KATEGORIE_LABEL[s.kategorie]}` : ''})
                  </option>
                ))}
            </select>
          </label>
          {steuersatzFehler && <span className="aa-inline-fehler">{steuersatzFehler}</span>}
        </div>
        <div className="aa-kopf-summe">
          <span className="aa-summe-label">Gesamtsumme</span>
          <span className="aa-summe-wert">{formatPrice(auftrag.gesamtsumme)}</span>
        </div>
      </div>

      {/* Prozess-Stepper */}
      <div className="aa-stepper-section">
        {auftrag.auftrag_typ && (
          <div className="aa-typ-label">{TYP_LABEL[auftrag.auftrag_typ] || auftrag.auftrag_typ}</div>
        )}
        {stepStati.length > 0 && (
          <div className="aa-stepper">
            {stepStati.map((s, idx) => {
              let cls = 'aa-step';
              if (istStorniert) cls += ' aa-step--inactive';
              else if (idx < currentIndex) cls += ' aa-step--done';
              else if (idx === currentIndex) cls += ' aa-step--current';
              else cls += ' aa-step--upcoming';
              return (
                <React.Fragment key={s}>
                  <div className={cls}>
                    <span className="aa-step-dot">{idx < currentIndex && !istStorniert ? '✓' : idx + 1}</span>
                    <span className="aa-step-label">{s}</span>
                  </div>
                  {idx < stepStati.length - 1 && <div className="aa-step-connector" />}
                </React.Fragment>
              );
            })}
          </div>
        )}
        {istStorniert && <div className="aa-storniert-banner">Auftrag storniert</div>}

        {naechsteSchritte.length > 0 && (
          <div className="aa-aktionen">
            {naechsteSchritte.map((schritt) => (
              <button
                key={schritt}
                className={`aa-aktion-btn${schritt === 'Storniert' ? ' aa-aktion-btn--danger' : ''}`}
                disabled={statusAktion !== null}
                onClick={() => handleStatusChange(schritt)}
              >
                {statusAktion === schritt ? 'Wird gesetzt…' : `→ ${schritt}`}
              </button>
            ))}
          </div>
        )}
        {statusFehler && <div className="aa-inline-fehler">{statusFehler}</div>}
        {downloadFehler && <div className="aa-inline-fehler">{downloadFehler}</div>}
      </div>

      {/* Karten-Grid */}
      <div className="aa-grid">
        {/* Positionen */}
        <div className="aa-card">
          <h3 className="aa-card-title">Positionen</h3>
          {auftrag.positionen && auftrag.positionen.length > 0 ? (
            <table className="aa-positionen-table">
              <thead>
                <tr>
                  <th>Artikel</th>
                  <th>Menge</th>
                  <th>Einzelpreis</th>
                  <th>Gesamt</th>
                </tr>
              </thead>
              <tbody>
                {auftrag.positionen.map((pos, idx) => (
                  <tr key={pos.position_id || idx}>
                    <td>
                      {pos.artikel?.bezeichnung || `Artikel #${pos.artikel_id}`}
                      {pos.regiebericht_nr && (
                        <span style={{ display: 'block', fontSize: 11, color: 'var(--ink-2)' }}>
                          aus {pos.regiebericht_nr}
                        </span>
                      )}
                    </td>
                    <td>{pos.menge}</td>
                    <td>{formatPrice(pos.preis_einzeln)}</td>
                    <td>{formatPrice(pos.preis_gesamt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="aa-empty">Keine Positionen</p>
          )}
          {auftrag.notizen && (
            <div className="aa-notizen">
              <strong>Notizen:</strong> {auftrag.notizen}
            </div>
          )}
        </div>

        {/* Regieberichte (AP 3.3) — Modul-Gating wie die Einsätze-Karte:
            der Bericht ist das Feldeinsatz-Artefakt (siehe Spec Abschnitt 8). */}
        {modulAktiv('einsatz') && (
          <RegieberichtKarte auftragId={auftragId} authToken={authToken} userRolle={userRolle} />
        )}

        {/* Nachkalkulation (AP 2.4) */}
        {kalkulation && (
          <div className="aa-card">
            <h3 className="aa-card-title">Nachkalkulation</h3>
            {kalkulation.hinweis && <p className="aa-empty">{kalkulation.hinweis}</p>}
            <div className="aa-kalk-summary">
              <div className="aa-kalk-item">
                <span className="aa-kalk-label">Soll</span>
                <span className="aa-kalk-value">{formatPrice(kalkulation.soll_gesamt)}</span>
              </div>
              <div className="aa-kalk-item">
                <span className="aa-kalk-label">Ist</span>
                <span className="aa-kalk-value">{formatPrice(kalkulation.ist_gesamt)}</span>
              </div>
              <div className="aa-kalk-item">
                <span className="aa-kalk-label">Deckungsbeitrag</span>
                <span
                  className={`aa-kalk-value aa-kalk-db aa-kalk-db--${deckungsbeitragAmpel(
                    kalkulation.deckungsbeitrag,
                    kalkulation.soll_gesamt
                  )}`}
                >
                  {formatPrice(kalkulation.deckungsbeitrag)}
                </span>
              </div>
            </div>
            <div className="aa-kalk-ist-detail">
              <span>Ist-Stunden: {kalkulation.ist_stunden.toFixed(2)} h ({formatPrice(kalkulation.ist_stunden_eur)})</span>
              <span>Ist-Material: {formatPrice(kalkulation.ist_material_eur)}</span>
            </div>
            {kalkulation.zeit_zeilen.length > 0 && (
              <>
                <button
                  className="aa-card-action aa-kalk-toggle"
                  onClick={() => setZeitDetailOffen((o) => !o)}
                >
                  {zeitDetailOffen ? 'Zeitbuchungen ausblenden' : 'Zeitbuchungen anzeigen'}
                </button>
                {zeitDetailOffen && (
                  <table className="aa-positionen-table aa-kalk-table">
                    <thead>
                      <tr>
                        <th>Datum</th>
                        <th>Mitarbeiter</th>
                        <th>Tätigkeit</th>
                        <th>Stunden</th>
                        <th>Betrag</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kalkulation.zeit_zeilen.map((z) => (
                        <tr key={z.buchung_id}>
                          <td>{formatDate(z.datum)}</td>
                          <td>{z.mitarbeiter_name || '-'}</td>
                          <td>{z.taetigkeit || '-'}</td>
                          <td>{z.stunden.toFixed(2)} h</td>
                          <td>{z.betrag_eur !== null ? formatPrice(z.betrag_eur) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
            {kalkulation.material_detail.length > 0 && (
              <>
                <button
                  className="aa-card-action aa-kalk-toggle"
                  onClick={() => setMaterialDetailOffen((o) => !o)}
                >
                  {materialDetailOffen ? 'Material-Detail ausblenden' : 'Material-Detail anzeigen'}
                </button>
                {materialDetailOffen && (
                  <table className="aa-positionen-table aa-kalk-table">
                    <thead>
                      <tr>
                        <th>Artikel</th>
                        <th>Menge Soll</th>
                        <th>Menge Ist</th>
                        <th>EUR Soll</th>
                        <th>EUR Ist</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kalkulation.material_detail.map((z) => (
                        <tr key={z.artikel_id}>
                          <td>{z.bezeichnung}</td>
                          <td>{z.menge_soll} {z.einheit || ''}</td>
                          <td>{z.menge_ist} {z.einheit || ''}</td>
                          <td>{formatPrice(z.eur_soll)}</td>
                          <td>{formatPrice(z.eur_ist)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        )}

        {/* Dokumente (Angebot/AB) - statusunabhängig immer verfügbar */}
        <div className="aa-card">
          <div className="aa-card-header">
            <h3 className="aa-card-title">Dokumente</h3>
          </div>
          <table className="aa-belege-tabelle">
            <thead>
              <tr>
                <th>Beleg</th>
                <th>Ansicht</th>
                <th>Format</th>
              </tr>
            </thead>
            <tbody>
              <tr onClick={angebotHerunterladen} title="Angebot als PDF herunterladen">
                <td>Angebot</td>
                <td
                  className="aa-belege-ansicht"
                  onClick={(e) => { e.stopPropagation(); openAngebotAnsicht(); }}
                  title="Beleg-Ansicht öffnen"
                >
                  Ansicht
                </td>
                <td className="aa-belege-format">PDF</td>
              </tr>
              <tr onClick={auftragsbestaetigungHerunterladen} title="Auftragsbestätigung als PDF herunterladen">
                <td>Auftragsbestätigung</td>
                <td
                  className="aa-belege-ansicht"
                  onClick={(e) => { e.stopPropagation(); openAuftragsbestaetigungAnsicht(); }}
                  title="Beleg-Ansicht öffnen"
                >
                  Ansicht
                </td>
                <td className="aa-belege-format">PDF</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Lieferscheine */}
        {modulAktiv('lieferschein') && (
          <div className="aa-card">
            <div className="aa-card-header">
              <h3 className="aa-card-title">Lieferscheine</h3>
              <button className="aa-card-action" onClick={lieferscheinHerunterladen}>PDF herunterladen</button>
            </div>
            {lieferstatusPositionen.length > 0 && (
              <div className="aa-fortschritt">
                <div className="aa-progress-bar">
                  <div className="aa-progress-fill" style={{ width: `${lieferPct}%` }} />
                </div>
                <span className="aa-fortschritt-label">{lieferPct}% geliefert</span>
              </div>
            )}
            {lieferscheinFehler && <div className="aa-inline-fehler">{lieferscheinFehler}</div>}
            {akte.lieferscheine.length === 0 ? (
              <p className="aa-empty">Keine Lieferscheine</p>
            ) : (
              <ul className="aa-liste">
                {akte.lieferscheine.map((ls) => (
                  <li key={ls.lieferschein_id} className="aa-liste-item">
                    <span className="aa-liste-nr">{ls.lieferschein_nr}</span>
                    <span className={`aa-status-badge aa-status-badge--${statusKind(ls.status)}`}>{ls.status}</span>
                    <span className="aa-liste-datum">{formatDate(ls.erstellt_am)}</span>
                    {['ENTWURF', 'VERSENDET'].includes(ls.status ?? '') && darfLieferscheineBuchen(rechte) && (
                      <button
                        className="aa-mini-btn"
                        onClick={() => lieferscheinStornieren(ls.lieferschein_id, ls.lieferschein_nr)}
                      >
                        Stornieren
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Fertigungsaufträge */}
        {modulAktiv('fertigungsauftrag') && (
          <div className="aa-card">
            <h3 className="aa-card-title">Fertigungsaufträge</h3>
            {akte.fertigungsauftraege.length === 0 ? (
              <p className="aa-empty">Keine Fertigungsaufträge</p>
            ) : (
              <ul className="aa-liste">
                {akte.fertigungsauftraege.map((fa) => (
                  <li key={fa.fa_id} className="aa-liste-item">
                    <span className="aa-liste-nr">{fa.fa_nr}</span>
                    <span className={`aa-status-badge aa-status-badge--${statusKind(fa.status)}`}>{fa.status}</span>
                    <span className="aa-liste-datum">
                      {fa.fortschritt_prozent !== null ? `${fa.fortschritt_prozent}%` : '-'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Rechnungen */}
        {modulAktiv('zahlung') && (
          <div className={`aa-card${rechnungDialogOffen || gutschriftFuer || stornoFuer ? ' aa-card--breit' : ''}`}>
            <div className="aa-card-header">
              <h3 className="aa-card-title">Rechnungen</h3>
              <button className="aa-card-action" onClick={() => { setRechnungMeldung(null); setGutschriftFuer(null); setStornoFuer(null); setRechnungDialogOffen(o => !o); }}>
                {rechnungDialogOffen ? 'Abbrechen' : '+ Rechnung erstellen'}
              </button>
            </div>
            {rechnungDialogOffen && (
              <RechnungErstellenDialog
                auftragId={auftragId}
                kundeId={auftrag.kunde_id ?? null}
                gesamtsumme={auftrag.gesamtsumme ?? null}
                gesamtrabattProzent={auftrag.gesamtrabatt_prozent ?? null}
                steuersatzProzent={auftrag.steuersatz_id != null
                  ? (steuersaetze.find(st => st.steuersatz_id === auftrag.steuersatz_id)?.prozent ?? null)
                  : null}
                steuersatzName={auftrag.steuersatz_id != null
                  ? (steuersaetze.find(st => st.steuersatz_id === auftrag.steuersatz_id)?.bezeichnung ?? `#${auftrag.steuersatz_id}`)
                  : 'je Artikel'}
                rechnungen={akte.rechnungen}
                onErstellt={() => { setRechnungDialogOffen(false); ladeAkte(); }}
                onAbbrechen={() => setRechnungDialogOffen(false)}
              />
            )}
            {gutschriftFuer && (
              <GutschriftDialog
                key={gutschriftFuer.ausgangsrechnung_id}
                bezug={{ id: gutschriftFuer.ausgangsrechnung_id, nummer: gutschriftFuer.nummer_anzeige }}
                onErstellt={rechnungDialogFertig}
                onAbbrechen={() => setGutschriftFuer(null)}
              />
            )}
            {stornoFuer && (
              <StornoDialog
                key={stornoFuer.ausgangsrechnung_id}
                rechnung={{
                  id: stornoFuer.ausgangsrechnung_id,
                  nummer: stornoFuer.nummer_anzeige,
                  rechnungsart: stornoFuer.rechnungsart,
                }}
                onFertig={rechnungDialogFertig}
                onAbbrechen={() => setStornoFuer(null)}
              />
            )}
            {rechnungMeldung && <div className="rd-ok" role="status">{rechnungMeldung}</div>}
            {sendenFehler && <div className="aa-inline-fehler">{sendenFehler}</div>}
            {akte.rechnungen.length === 0 ? (
              <p className="aa-empty">Keine Rechnungen</p>
            ) : (
              <ul className="aa-liste aa-liste--rechnungen">
                {akte.rechnungen.map((r) => {
                  const korrektur = istKorrektur(r.rechnungsart);
                  const bezug = bezugText(r, id =>
                    akte.rechnungen.find(x => x.ausgangsrechnung_id === id)?.nummer_anzeige);
                  return (
                    <li key={r.ausgangsrechnung_id} className="aa-liste-item">
                      <span className="aa-liste-nr">{r.nummer_anzeige}</span>
                      {normalisiereArt(r.rechnungsart) !== 'RECHNUNG' && <RechnungsartBadge art={r.rechnungsart} />}
                      {bezug && <span className="rd-bezug">{bezug}</span>}
                      <span className={`aa-status-badge aa-status-badge--${statusKind(r.status)}`}>{r.status}</span>
                      <span className={`aa-liste-datum${korrektur ? ' rd-betrag--korrektur' : ''}`}>
                        {betragAnzeige(r.brutto_gesamt, r.rechnungsart)}
                      </span>
                      {!korrektur && r.status !== 'ENTWURF' && r.status !== 'STORNIERT' && (
                        <span className={`aa-zahlung-badge${r.offen <= 0 ? ' aa-zahlung-badge--bezahlt' : ''}`}>
                          {r.offen <= 0 ? 'Bezahlt' : `${formatPrice(r.offen)} offen`}
                        </span>
                      )}
                      <button className="aa-mini-btn" onClick={() => openRechnungAnsicht(r)}>Ansehen</button>
                      {r.status === 'ENTWURF' && darfRechnungFestschreiben(rechte, r.rechnungsart) && (
                        <>
                          <button
                            className="aa-mini-btn"
                            onClick={() => rechnungSenden(r)}
                            disabled={sendenId === r.ausgangsrechnung_id}
                          >
                            {sendenId === r.ausgangsrechnung_id ? 'Sendet…' : 'Senden'}
                          </button>
                          <button
                            className="aa-mini-btn"
                            onClick={() => rechnungFestschreiben(r)}
                            disabled={sendenId === r.ausgangsrechnung_id}
                            title="Nummer vergeben und festschreiben, ohne E-Mail"
                          >
                            Festschreiben (ohne Mail)
                          </button>
                        </>
                      )}
                      {r.status === 'ENTWURF' && darfBelegeSenden(rechte) && (
                        <button
                          className="aa-mini-btn"
                          onClick={() => rechnungEntwurfVerwerfen(r)}
                          disabled={sendenId === r.ausgangsrechnung_id}
                        >
                          Verwerfen
                        </button>
                      )}
                      {kannGutschreiben({ rechnungsart: r.rechnungsart, status: r.status })
                        && darfKorrekturbelege(rechte) && (
                        <button
                          className="aa-mini-btn"
                          onClick={() => { setStornoFuer(null); setRechnungDialogOffen(false); setRechnungMeldung(null); setGutschriftFuer(r); }}
                          title="Gutschrift (Rechnungskorrektur) zu dieser Rechnung erstellen"
                        >
                          Gutschrift erstellen
                        </button>
                      )}
                      {kannStornieren({ rechnungsart: r.rechnungsart, status: r.status })
                        && darfKorrekturbelege(rechte) && (
                        <button
                          className="aa-mini-btn"
                          onClick={() => { setGutschriftFuer(null); setRechnungDialogOffen(false); setRechnungMeldung(null); setStornoFuer(r); }}
                          title="Stornorechnung erstellen und diese Rechnung aufheben"
                        >
                          Stornieren
                        </button>
                      )}
                      <button className="aa-mini-btn" onClick={() => rechnungHerunterladen(r)}>PDF</button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* Vertrag (aus diesem Auftrag erzeugt) */}
        {modulAktiv('vertrag') && auftrag.vertrag && (
          <div className="aa-card">
            <h3 className="aa-card-title">Vertrag</h3>
            <ul className="aa-liste">
              <li className="aa-liste-item">
                <span className="aa-liste-nr">{auftrag.vertrag.vertrag_nr || `#${auftrag.vertrag.vertrag_id}`}</span>
                <span className={`aa-status-badge aa-status-badge--${vertragStatusKind(auftrag.vertrag.status)}`}>
                  {auftrag.vertrag.status}
                </span>
                <span>Version {auftrag.vertrag.aktuelle_version_label ?? '—'}</span>
                <span className="aa-liste-datum">
                  Nächster Stichtag: {formatDate(auftrag.vertrag.naechste_abrechnung_am)}
                </span>
                {onOpenVertrag && (
                  <button className="aa-mini-btn" onClick={() => onOpenVertrag(auftrag.vertrag!.vertrag_id)}>
                    Vertragsakte öffnen
                  </button>
                )}
              </li>
            </ul>
          </div>
        )}

        {/* Retouren */}
        {modulAktiv('retoure') && (
          <div className="aa-card">
            <h3 className="aa-card-title">Retouren</h3>
            {akte.retouren.length === 0 ? (
              <p className="aa-empty">Keine Retouren</p>
            ) : (
              <ul className="aa-liste">
                {akte.retouren.map((ret) => (
                  <li key={ret.retoure_id} className="aa-liste-item">
                    <span className="aa-liste-nr">{ret.retoure_nr}</span>
                    <span className={`aa-status-badge aa-status-badge--${statusKind(ret.status)}`}>{ret.status}</span>
                    <span className="aa-liste-datum">{ret.grund || '-'}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Sendungen */}
        {modulAktiv('sendung') && (
          <div className="aa-card">
            <h3 className="aa-card-title">Sendungen</h3>
            {akte.sendungen.length === 0 ? (
              <p className="aa-empty">Keine Sendungen</p>
            ) : (
              <ul className="aa-liste">
                {akte.sendungen.map((s) => (
                  <li key={s.sendung_id} className="aa-liste-item">
                    <span className="aa-liste-nr">{s.tracking_nr || `Sendung #${s.sendung_id}`}</span>
                    <span className={`aa-status-badge aa-status-badge--${statusKind(s.status)}`}>{s.status}</span>
                    <span className="aa-liste-datum">{s.spediteur || '-'}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Einsätze */}
        {modulAktiv('einsatz') && (
          <div className="aa-card">
            <h3 className="aa-card-title">Einsätze</h3>
            {akte.einsaetze.length === 0 ? (
              <p className="aa-empty">Keine Einsätze</p>
            ) : (
              <ul className="aa-liste">
                {akte.einsaetze.map((ei) => (
                  <li key={ei.einsatz_id} className="aa-liste-item">
                    <span className="aa-liste-nr">{ei.mitarbeiter_name || `Einsatz #${ei.einsatz_id}`}</span>
                    <span className={`aa-status-badge aa-status-badge--${einsatzStatusKind(ei.status)}`}>{ei.status}</span>
                    <span className="aa-liste-datum">{formatDate(ei.beginn)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

      </div>

      <DokumenteKarte auftragId={auftragId} authToken={authToken} />

      {/* Notizen/Aufgaben */}
      <div className="aa-anhang-section">
        <NotizenAufgaben entitaetTyp="auftrag" entitaetId={auftragId} authToken={authToken} />
      </div>

      {/* Anhänge */}
      <div className="aa-anhang-section">
        <AnhangPanel entitaetTyp="auftrag" entitaetId={auftragId} authToken={authToken} />
      </div>

      {/* Timeline */}
      <div className="aa-timeline-section">
        <Timeline entitaetTyp="auftrag" entitaetId={auftragId} authToken={authToken} />
      </div>

      {belegAnsicht && (
        <BelegAnsichtModal
          url={belegAnsicht.url}
          title={belegAnsicht.title}
          kontext={{}}
          onClose={() => setBelegAnsicht(null)}
        />
      )}

      {quelleBerichtOffen && auftrag.quelle_regiebericht && (
        <RegieberichtForm
          {...(auftrag.quelle_regiebericht.auftrag_id != null
            ? { auftragId: auftrag.quelle_regiebericht.auftrag_id }
            : { projektId: auftrag.quelle_regiebericht.projekt_id as number })}
          regieberichtId={auftrag.quelle_regiebericht.id}
          authToken={authToken}
          userRolle={userRolle}
          onClose={() => setQuelleBerichtOffen(false)}
          onChanged={ladeAkte}
        />
      )}
    </div>
  );
};

export default AuftragsAkte;
